import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { GdsServiceProductStatus } from '@websky/graphql';
import { ServiceOptionItem } from '../../types';
import { useTheme } from '../../../theme';
import Money from '../../../Money';
import { capitalizeFirstLetter } from '../../../utils';
import { isShowInsurancesByPassengers } from '../../utils';
import { ServiceSummaryType } from '../../../features/service-summary/model/types';
import { CdnIcon } from '@shared/ui';
import { Checkbox } from '../../../BaseComponents';
import ServiceSummaryDownloadButton from '../ServiceSummaryDownloadButton/ServiceSummaryDownloadButton';

interface Props {
	services: ServiceOptionItem[];
	notification?: React.ReactNode;
	mode?: 'default' | 'aggregator';
	serviceType?: ServiceSummaryType;
	selectedIds?: string[];
	disabled?: boolean;
	onSelect?: (item: ServiceOptionItem) => void;
}

const ServiceSummaryGroup: React.FC<Props> = ({
	services,
	notification,
	mode = 'default',
	serviceType,
	selectedIds = [],
	disabled,
	onSelect
}) => {
	const { t } = useTranslation('Cart');
	const { ServiceSummaryOption: css } = useTheme('ServiceSummary');

	const isInsurancesByPassengersMode = serviceType === 'Insurance' && isShowInsurancesByPassengers(mode);

	return (
		<div className={css.options}>
			{services.map((item, index) => {
				const isBooked = item.status === GdsServiceProductStatus.Booked;
				const isRequested = item.status === GdsServiceProductStatus.Requested;
				const showControls = item.downloadUrl || item.onDownload || item.included;
				const price = isInsurancesByPassengersMode ? item.passengerPrice : item.price;

				const optionName = isInsurancesByPassengersMode ? capitalizeFirstLetter(item.passengerName) : item.name;
				const OptionNameTag = onSelect ? 'label' : 'span';
				const checkboxId = onSelect ? `service-summary-option-${item.id}-${index}` : undefined;

				return (
					<div
						className={cn(css.option, {
							[css.option_icon]: item.icon,
							[css.option_booked]: isBooked,
							[css.option_requested]: isRequested,
							[css.option_rejected]: item.status === GdsServiceProductStatus.Rejected,
							[css.option_canceled]: item.status === GdsServiceProductStatus.Canceled,
							[css.option_problematic]: item.status === GdsServiceProductStatus.Problematic,
							[css.option_included]: item.included
						})}
						key={index}
						data-index={index}
					>
						{onSelect && (
							<Checkbox
								checked={selectedIds.includes(item.id)}
								disabled={disabled}
								slotProps={{ input: { id: checkboxId } }}
								onChange={() => onSelect(item)}
							/>
						)}
						{item.icon ? <div className={css.option__icon}>{item?.icon}</div> : null}
						<OptionNameTag
							className={cn(css.option__name, { [css.option__name_selectable]: onSelect && !disabled })}
							htmlFor={checkboxId}
						>
							{optionName}
						</OptionNameTag>

						{item.description && <span className={css.option__description}>{item?.description}</span>}

						{showControls && (
							<div className={css.option__controls}>
								{(item.downloadUrl || item.onDownload) && (
									<ServiceSummaryDownloadButton
										onDownload={item.onDownload}
										downloadUrl={item.downloadUrl}
										allPassengersDownloadUrls={item.allPassengersDownloadUrls}
										useSingleDownloadUrl={
											isInsurancesByPassengersMode || serviceType !== 'Insurance'
										}
									/>
								)}
								{item.included && (
									<div className={css.option__iconIncluded}>
										<CdnIcon icon={'Info/Item Icluded'} />
									</div>
								)}
							</div>
						)}

						{price && !showControls && (
							<div className={css.option__price}>
								<div className={css.option__price_money}>
									{item.priceFrom && t('Checkout:from')}
									<Money moneyClassName={css.option__money} money={price} />
									{!!item.onDelete && !isRequested && (
										<div className={css.option__price_icon} onClick={item.onDelete}>
											<CdnIcon icon={'Action/Close-Clear'} />
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				);
			})}
			{notification}
		</div>
	);
};

export default ServiceSummaryGroup;
